import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const STA_MANUAL_KEY = "sta_manual";
const STA_MANUAL_BH_KEY = "sta_manual_block_time_minutes";

/**
 * 把轮档分钟收成大于 0 的整数。缺值、0、负数都当作没有有效轮档。
 */
export function parsePositiveMinutes(value) {
    if (value === undefined || value === null || value === "") return null;
    const minutes = typeof value === "number" ? value : parseInt(value, 10);
    if (!Number.isFinite(minutes) || minutes <= 0) return null;
    return minutes;
}

/**
 * 先截到整分钟再比、再加。秒不单独造成对不上。
 */
export function truncateIsoToMinute(iso) {
    if (!iso) return null;
    const parsed = dayjs.utc(iso);
    if (!parsed.isValid()) return null;
    return parsed.startOf("minute").format("YYYY-MM-DDTHH:mm:ss") + "Z";
}

/**
 * 当前绑定航路上的计划飞多久。禁止用到达减起飞冒充。
 */
export function getCurrentBlockTimeMinutes(flight) {
    if (!flight) return null;
    const snapshotBh = flight.additional_info?.flight_route?.block_time_minutes;
    return (
        parsePositiveMinutes(flight.block_time_minutes) ??
        parsePositiveMinutes(flight.flight_route_block_time_minutes) ??
        parsePositiveMinutes(flight.min_block_time_minutes) ??
        parsePositiveMinutes(snapshotBh)
    );
}

/**
 * 到达 = 起飞 + 轮档。用完整时戳加分钟，例如 17:25 + 7:15 = 次日 00:40。
 * 禁止复用「钟面到达早于起飞就加一天」。
 */
export function computeStaFromStdAndBlockTime(std, blockTimeMinutes) {
    const truncatedStd = truncateIsoToMinute(std);
    const minutes = parsePositiveMinutes(blockTimeMinutes);
    if (!truncatedStd || minutes == null) return null;
    return dayjs.utc(truncatedStd).add(minutes, "minute").format("YYYY-MM-DDTHH:mm:ss") + "Z";
}

export function getStaMinusStdMinutes(std, sta) {
    const truncatedStd = truncateIsoToMinute(std);
    const truncatedSta = truncateIsoToMinute(sta);
    if (!truncatedStd || !truncatedSta) return null;
    return dayjs.utc(truncatedSta).diff(dayjs.utc(truncatedStd), "minute");
}

export function isStaAlignedWithBlockTime(std, sta, blockTimeMinutes) {
    const minutes = parsePositiveMinutes(blockTimeMinutes);
    const diff = getStaMinusStdMinutes(std, sta);
    if (minutes == null || diff == null) return false;
    return diff === minutes;
}

function hasValidStdStaAndBlockTime(flight) {
    if (!flight) return false;
    const bh = getCurrentBlockTimeMinutes(flight);
    return Boolean(truncateIsoToMinute(flight.std) && truncateIsoToMinute(flight.sta) && bh != null);
}

/**
 * 到达减去起飞的整分钟数，不等于当前轮档。缺起飞或缺轮档不算对不上。
 */
export function isStaMismatch(flight) {
    if (!hasValidStdStaAndBlockTime(flight)) return false;
    return !isStaAlignedWithBlockTime(flight.std, flight.sta, getCurrentBlockTimeMinutes(flight));
}

export function readStaManualMark(flight) {
    const info = flight?.additional_info;
    if (!info || typeof info !== "object") {
        return { staManual: false, staManualBlockTimeMinutes: null };
    }
    const staManual = info[STA_MANUAL_KEY] === true;
    const staManualBlockTimeMinutes = parsePositiveMinutes(info[STA_MANUAL_BH_KEY]);
    return { staManual, staManualBlockTimeMinutes };
}

/**
 * 手填只在记下的轮档等于当前轮档时仍有效。
 * 传入 currentBlockMinutes 时，用新轮档判断（换航路 / 刷新 / 导入）。
 */
export function isManualStaStillValid(flight, currentBlockMinutes = getCurrentBlockTimeMinutes(flight)) {
    const { staManual, staManualBlockTimeMinutes } = readStaManualMark(flight);
    const current = parsePositiveMinutes(currentBlockMinutes);
    return staManual && staManualBlockTimeMinutes != null && current != null && staManualBlockTimeMinutes === current;
}

export function hasExpiredStaManual(flight, currentBlockMinutes = getCurrentBlockTimeMinutes(flight)) {
    const { staManual, staManualBlockTimeMinutes } = readStaManualMark(flight);
    if (!staManual) return false;
    const current = parsePositiveMinutes(currentBlockMinutes);
    return staManualBlockTimeMinutes == null || current == null || staManualBlockTimeMinutes !== current;
}

/**
 * 漏改：对不上且不是仍有效手填；过期手填一律算漏改。
 * 记下的轮档已经变了，即使到达碰巧已等于起飞加新轮档，也要进确认条。
 * 否则失效标记会留在航班附加信息里，轮档再变回去时会被当成有效手填。
 * 没有当前轮档时不算漏改：不能按轮档改到达，也不拦保存。
 */
export function isStaLeak(flight) {
    if (getCurrentBlockTimeMinutes(flight) == null) return false;
    if (hasExpiredStaManual(flight)) return true;
    if (!isStaMismatch(flight)) return false;
    return !isManualStaStillValid(flight);
}

function cloneAdditionalInfo(flight) {
    const info = flight?.additional_info;
    if (!info || typeof info !== "object") return {};
    const cloned = { ...info };
    if (info.flight_route && typeof info.flight_route === "object") {
        cloned.flight_route = { ...info.flight_route };
    }
    return cloned;
}

export function clearStaManual(flight) {
    if (!flight) return flight;
    const info = cloneAdditionalInfo(flight);
    if (!(STA_MANUAL_KEY in info) && !(STA_MANUAL_BH_KEY in info)) {
        return flight;
    }
    delete info[STA_MANUAL_KEY];
    delete info[STA_MANUAL_BH_KEY];
    const nextInfo = Object.keys(info).length > 0 ? info : undefined;
    return { ...flight, additional_info: nextInfo };
}

export function markStaManual(flight) {
    if (!flight) return flight;
    const bh = getCurrentBlockTimeMinutes(flight);
    if (bh == null) return flight;
    const info = cloneAdditionalInfo(flight);
    info[STA_MANUAL_KEY] = true;
    info[STA_MANUAL_BH_KEY] = bh;
    return { ...flight, additional_info: info };
}

/**
 * 失效手填在写出前清掉这两个字段，避免下次打开误判。
 */
export function sanitizeStaManualForSave(flight) {
    if (!flight) return flight;
    const { staManual } = readStaManualMark(flight);
    if (!staManual) return clearStaManual(flight);
    if (!isManualStaStillValid(flight)) return clearStaManual(flight);
    return flight;
}

export function applyBlockTimeRedundancy(flight, minutes) {
    const normalized = parsePositiveMinutes(minutes);
    return {
        ...flight,
        block_time_minutes: normalized,
        min_block_time_minutes: normalized,
        flight_route_block_time_minutes: normalized,
    };
}

export function syncRouteSnapshotBlockTime(flight, minutes) {
    const info = flight?.additional_info;
    if (!info?.flight_route || typeof info.flight_route !== "object") return flight;
    const normalized = parsePositiveMinutes(minutes);
    return {
        ...flight,
        additional_info: {
            ...cloneAdditionalInfo(flight),
            flight_route: {
                ...info.flight_route,
                block_time_minutes: normalized,
            },
        },
    };
}

/**
 * 起飞不动，到达改成起飞加当前轮档。缺有效轮档时到达保持原值。
 */
export function applyStaFromCurrentBlockTime(flight) {
    if (!flight) return flight;
    const bh = getCurrentBlockTimeMinutes(flight);
    const newSta = computeStaFromStdAndBlockTime(flight.std, bh);
    if (!newSta) return flight;
    let next = { ...flight, sta: newSta };
    next = applyBlockTimeRedundancy(next, bh);
    next = syncRouteSnapshotBlockTime(next, bh);
    return next;
}

/**
 * 刷新基础数据：只有拿到大于 0 的轮档才覆盖。缺轮档保留页面上的到达、轮档和手填。
 */
export function applyMasterRouteBlockTimeRefresh(flight, newBlockMinutes) {
    if (!flight) return flight;
    const normalized = parsePositiveMinutes(newBlockMinutes);
    if (normalized == null) return flight;
    return applyRouteBlockTimeStaPolicy(flight, normalized);
}

/**
 * 换航路 / 刷新 / 指定导入：
 * 仍有效手填且轮档没变 → 到达保持手填值；
 * 否则手填作废，到达按新轮档重写。
 */
export function applyRouteBlockTimeStaPolicy(flight, newBlockMinutes) {
    if (!flight) return flight;
    const normalized = parsePositiveMinutes(newBlockMinutes);
    if (normalized == null) {
        return applyBlockTimeRedundancy(flight, null);
    }
    const keepManual = isManualStaStillValid(flight, normalized);
    let next = applyBlockTimeRedundancy(flight, normalized);
    next = syncRouteSnapshotBlockTime(next, normalized);
    if (keepManual) {
        return next;
    }
    next = clearStaManual(next);
    return applyStaFromCurrentBlockTime(next);
}

/**
 * 表单里改起飞：等于放弃手填，到达改成新起飞加当前轮档。
 */
export function applyStdChangeAbandoningManual(flight, newStd) {
    if (!flight || !newStd) return flight;
    let next = { ...flight, std: newStd, flight_date: newStd };
    next = clearStaManual(next);
    return applyStaFromCurrentBlockTime(next);
}

export function findStaLeaks(flights) {
    if (!Array.isArray(flights)) return [];
    return flights.filter((flight) => !flight?.isAddHeaderRow && !flight?.isAddRow && isStaLeak(flight));
}

export function describeStaLeak(flight) {
    const bh = getCurrentBlockTimeMinutes(flight);
    const expectedSta = computeStaFromStdAndBlockTime(flight.std, bh);
    const currentSta = truncateIsoToMinute(flight.sta);
    const expectedTruncated = truncateIsoToMinute(expectedSta);
    const deltaMinutes =
        currentSta && expectedTruncated ? Math.abs(dayjs.utc(currentSta).diff(dayjs.utc(expectedTruncated), "minute")) : null;
    return {
        id: flight.id,
        flight_number: flight.flight_number,
        departure_airport: flight.departure_airport,
        arrival_airport: flight.arrival_airport,
        flight_date: flight.flight_date,
        std: flight.std,
        currentSta: flight.sta,
        expectedSta,
        deltaMinutes,
        blockTimeMinutes: bh,
        expiredManual: hasExpiredStaManual(flight),
    };
}

export function formatUtcClock(iso) {
    const truncated = truncateIsoToMinute(iso);
    if (!truncated) return "--:--";
    return dayjs.utc(truncated).format("HH:mm");
}

export function formatUtcDateClock(iso) {
    const truncated = truncateIsoToMinute(iso);
    if (!truncated) return "--";
    return dayjs.utc(truncated).format("MM-DD HH:mm");
}

export { STA_MANUAL_KEY, STA_MANUAL_BH_KEY };
